import { Injectable } from '@angular/core';

/**
 * Servizio per il calcolo dei totali del preventivo
 */
@Injectable({
  providedIn: 'root'
})
export class SummaryService {

  constructor() { }

  /**
   * Calcola il totale del preventivo basato sui dati forniti
   * @param allDays - Dati di tutti i giorni del preventivo
   * @param extra - Dati extra (fees e insurances)
   * @returns Oggetto contenente tutti i totali calcolati
   */
  public calculateTotals(allDays: any, extra: any): SummaryTotals {
    const flightsTotal = this.calculateFlightsTotal(allDays.flights);
    const flightsTotalTax = this.calculateFlightsTotalTax(allDays.flights);
    const hotelsTotal = this.calculateHotelsTotal(allDays.hotels);
    const carsTotal = this.calculateCarsTotal(allDays.cars);
    const transfersTotal = this.calculateTransfersTotal(allDays.transfers);
    const transfersSuggestedTotal = this.calculateTransfersSuggestedTotal(allDays.transfersSuggested);
    const optionalsTotal = this.calculateOptionalsTotal(allDays.optionals);
    const optionalsSuggestedTotal = this.calculateOptionalsSuggestedTotal(allDays.optionalsSuggested);
    const escortedsTotal = this.calculateEscortedsTotal(allDays.escorteds);
    const feeTotal = this.calculateFeeTotal(extra.fees);
    const insuranceTotal = this.calculateInsuranceTotal(extra.insurances);

    return {
      totalGroundService: hotelsTotal + carsTotal + transfersTotal + optionalsTotal + escortedsTotal,
      totalGroundNoOptional: hotelsTotal + carsTotal + transfersTotal,
      totalFlightsService: flightsTotal,
      totalFlightsTaxService: flightsTotalTax,
      totalActivities: optionalsTotal + escortedsTotal,
      totalFeeInsuranceService: insuranceTotal + feeTotal,
      transfersSuggestedTotal: transfersSuggestedTotal,
      optionalsSuggestedTotal: optionalsSuggestedTotal,
      hotelsTotal,
      carsTotal,
      transfersTotal,
      optionalsTotal,
      escortedsTotal,
      feeTotal,
      insuranceTotal
    };
  }

  public getTotalAmount(totalGroundService: any, totalFlightsService: any, advFees: any, totalFeeInsuranceService: any): number {
    const advFee = (advFees?.feeFlight || 0) +
                    (advFees?.feeLandServices || 0);

    return totalGroundService +
           totalFlightsService +
           advFee +
           totalFeeInsuranceService;
  }

  private calculateFlightsTotal(flights: any[]): number {
    return flights.reduce(
      (accumulator: { total: number; seenSequences: Set<number> }, flight: any) => {
        if (flight.sequenceNumber === "0" || !accumulator.seenSequences.has(flight.sequenceNumber)) {
          // Aggiungi il sequenceNumber a `seenSequences` solo se diverso da 0
          if (flight.sequenceNumber !== "0") {
            accumulator.seenSequences.add(flight.sequenceNumber);
          }
          return {
            total: accumulator.total + flight.amountaftertax + (flight.adv_markup ? flight.adv_markup : 0),
            seenSequences: accumulator.seenSequences,
          };
        }
        return accumulator;
      },
      { total: 0, seenSequences: new Set<number>() }
    ).total;
  }

  private calculateFlightsTotalTax(flights: any[]): number {
    return flights.reduce((accumulator: { total: number, seenSequences: Set<number> }, flight: any) => {
      if (!accumulator.seenSequences.has(flight.sequenceNumber) && 'taxes' in flight) {
        accumulator.seenSequences.add(flight.sequenceNumber);
        return {
          total: accumulator.total + flight.taxes,
          seenSequences: accumulator.seenSequences
        };
      }
      return accumulator;
    }, { total: 0, seenSequences: new Set<number>() }).total;
  }

  /**
   * Calcola il totale di un singolo item (amountaftertax + adv_markup)
   */
  private calculateItemTotal(item: any): number {
    return item.amountaftertax + (item.adv_markup || 0);
  }

  /**
   * Calcola il totale di un array di items usando la logica standard
   */
  private calculateSimpleArrayTotal(items: any[]): number {
    return items.reduce((total: number, item: any) => total + this.calculateItemTotal(item), 0);
  }

  /**
   * Calcola il totale per strutture con optionals[0].items
   */
  private calculateOptionalsStructureTotal(items: any[]): number {
    return items.reduce((total: number, item: any) => {
      return total + item.optionals[0].items.reduce((itemsTotal: number, subItem: any) => {
        return itemsTotal + this.calculateItemTotal(subItem);
      }, 0);
    }, 0);
  }

  private calculateHotelsTotal(hotels: any[]): number {
    return hotels.reduce((total: number, hotel: any) => {
      return total + hotel.hotels[0].rooms.reduce((roomsTotal: number, room: any) => {
        return roomsTotal + this.calculateItemTotal(room);
      }, 0);
    }, 0);
  }

  private calculateCarsTotal(cars: any[]): number {
    return cars.reduce((total: number, vehicle: any) => {
      return total + vehicle.vehicles.reduce((carsTotal: number, car: any) => {
        return carsTotal + this.calculateItemTotal(car);
      }, 0);
    }, 0);
  }

  private calculateTransfersTotal(transfers: any[]): number {
    return this.calculateOptionalsStructureTotal(transfers);
  }

  private calculateTransfersSuggestedTotal(transfersSuggested: any[]): number {
    return this.calculateOptionalsStructureTotal(transfersSuggested);
  }

  private calculateOptionalsTotal(optionals: any[]): number {
    return this.calculateOptionalsStructureTotal(optionals);
  }

  private calculateOptionalsSuggestedTotal(optionalsSuggested: any[]): number {
    return this.calculateOptionalsStructureTotal(optionalsSuggested);
  }

  private calculateEscortedsTotal(escorteds: any[]): number {
    return this.calculateOptionalsStructureTotal(escorteds);
  }

  private calculateFeeTotal(fees: any[]): number {
    return this.calculateSimpleArrayTotal(fees);
  }

  private calculateInsuranceTotal(insurances: any[]): number {
    return this.calculateSimpleArrayTotal(insurances);
  }
}

/**
 * Interfaccia per i totali del preventivo
 */
export interface SummaryTotals {
  totalGroundService: number;
  totalGroundNoOptional: number;
  totalFlightsService: number;
  totalFlightsTaxService: number;
  totalActivities: number;
  totalFeeInsuranceService: number;
  transfersSuggestedTotal: number;
  optionalsSuggestedTotal: number;
  hotelsTotal: number;
  carsTotal: number;
  transfersTotal: number;
  optionalsTotal: number;
  escortedsTotal: number;
  feeTotal: number;
  insuranceTotal: number;
}